import {
  React,
  Box,
  Grid,
  Typography,
  TextField,
  Button,
  Formik,
  Form,
  Yup,
  ChevronLeft,
  useNavigate,
  IconButton,
  FormikHelpers,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Paper,
  Alert,
  CircularProgress,
  useState,
  useEffect,
  Tooltip,
} from '../../utils/globalImports';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { useGetClientsByCustomerIdQuery, useGetClientByIdQuery } from '../../api/clientApiSlice';
import { CustomerResponse } from '../../store/types';
import IconSelector from '../../components/selector/IconSelector';
import { useTranslation } from 'react-i18next';
import { Maximize2 } from 'lucide-react';
import FullScreenContentDialog from '../../components/dialogbox/FullScreenContentDialog';

interface ExtendedAccountResponse {
  role?: string;
  email?: string;
  customer_id?: string;
  client_id?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`data-agent-tabpanel-${index}`}
      aria-labelledby={`data-agent-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `data-agent-tab-${index}`,
    'aria-controls': `data-agent-tabpanel-${index}`,
  };
}

interface AddEditDataAgentUIFormProps {
  entityType: string;
  initialValues: Record<string, any>;
  dataAgentId?: string;
  validationSchema: Yup.ObjectSchema<any>;
  onSubmit: (values: any, formikHelpers: FormikHelpers<any>) => void;
  onClose?: () => void;
  customerData?: { customer_id: string; name: string };
  customerName?: string;
  customerList?: CustomerResponse[];
  customerLoading?: boolean;
}

const getErrorMessage = (error: unknown): string => {
  if (typeof error === 'string') {
    return error;
  }
  if (Array.isArray(error)) {
    return error.filter((e) => typeof e === 'string').join(', ');
  }
  return '';
};

const AddEditDataAgentUIForm: React.FC<AddEditDataAgentUIFormProps> = ({
  entityType,
  initialValues,
  validationSchema,
  onSubmit,
  onClose,
  dataAgentId,
  customerData: propCustomerData,
  customerName: propCustomerName,
  customerList = [],
  customerLoading = false,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  const [fullScreenField, setFullScreenField] = useState<'instructions' | 'system_prompt' | null>(null);

  // Get auth state and customer ID for API headers
  const auth = useSelector((state: RootState) => state.auth);
  const account = auth.account as ExtendedAccountResponse | null;
  const customerId = account?.customer_id;
  // Get user role from Redux (auth state already retrieved above)
  const userRole = (account?.role || '').trim().toLowerCase();
  const { selectedCustomerId, selectedClientId } = useSelector((state: RootState) => state.vaultilake);
  const shouldHideCustomerClientFields = selectedCustomerId && selectedClientId;
  const clientId = account?.client_id;

  // Determine if fields should be disabled based on role
  const isCustomerIdDisabled = userRole === 'customeradmin' || userRole === 'clientadmin';
  const isClientIdDisabled = userRole === 'clientadmin' || entityType === 'edit';

  const customerData =
    propCustomerData ||
    (userRole === 'customeradmin' || userRole === 'clientadmin'
      ? {
          customer_id: account?.customer_id || '',
          name: propCustomerName || account?.customer_id || '',
        }
      : undefined);

  // Fetch client data for clientadmin
  const { data: clientData, isLoading: clientLoading } = useGetClientByIdQuery(
    { clientId: clientId },
    {
      skip: userRole !== 'clientadmin' || !clientId,
    },
  );

  // Check if we're in edit mode
  const isEditMode = entityType === 'edit';

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Reset to first tab if switching from edit to add mode
  useEffect(() => {
    if (!isEditMode && tabValue > 0) {
      setTabValue(0);
    }
  }, [isEditMode, tabValue]);

  // Sort customer list alphabetically
  const sortedCustomerList = [...customerList].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
  );

  // Customer and Client Fields Component (to avoid duplication)
  const CustomerClientFields = ({ values, handleChange, handleBlur, errors, touched, setFieldValue }: any) => {
    // Fetch clients based on selected customer
    const { data: dynamicClientList = [], isLoading: dynamicClientLoading } = useGetClientsByCustomerIdQuery(
      values.customer_id,
      {
        skip: !values.customer_id || userRole === 'clientadmin',
      },
    );

    const sortedFilteredClientList = [...dynamicClientList].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
    );

    const handleCustomerChange = (event: SelectChangeEvent<string>) => {
      const newCustomerId = event.target.value as string;
      setFieldValue('customer_id', newCustomerId);
      if (newCustomerId !== values.customer_id) {
        setFieldValue('client_id', '');
      }
    };

    return (
      <>
        {/* Customer Name Field */}
        {userRole !== 'clientadmin' && (
          <Grid size={6}>
            <Typography component="p" sx={{ mb: 2 }}>
              {t('dataAgents.form.customerName')}
              <Box component="span" sx={{ color: 'red', ml: 0.5 }}>
                *
              </Box>
            </Typography>
            {isCustomerIdDisabled || customerData ? (
              <>
                <TextField
                  value={propCustomerName || customerData?.name || ''}
                  disabled
                  fullWidth
                  size="small"
                  placeholder={t('dataAgents.form.customerNamePlaceholder')}
                  error={Boolean(touched.customer_id && errors.customer_id)}
                  helperText={touched.customer_id && errors.customer_id ? getErrorMessage(errors.customer_id) : ''}
                />
                <input type="hidden" name="customer_id" value={customerData?.customer_id || values.customer_id || ''} />
              </>
            ) : (
              <Select
                name="customer_id"
                value={values.customer_id}
                onChange={handleCustomerChange}
                onBlur={handleBlur}
                fullWidth
                size="small"
                displayEmpty
                disabled={customerLoading}
                error={Boolean(touched.customer_id && errors.customer_id)}
                MenuProps={{
                  PaperProps: { style: { maxHeight: 400, zIndex: 1300 } },
                  disablePortal: false,
                }}
              >
                <MenuItem value="">{t('dataAgents.form.selectCustomer')}</MenuItem>
                {sortedCustomerList.map((customer) => (
                  <MenuItem key={customer.customer_id} value={customer.customer_id}>
                    {customer.name}
                  </MenuItem>
                ))}
              </Select>
            )}
            {touched.customer_id && errors.customer_id && !isCustomerIdDisabled && !customerData && (
              <Typography sx={{ color: 'error.main', fontSize: '0.75rem', mt: 1 }}>
                {getErrorMessage(errors.customer_id)}
              </Typography>
            )}
          </Grid>
        )}

        {/* Client Name Field */}
        <Grid size={userRole === 'clientadmin' ? 12 : 6}>
          <Typography component="p" sx={{ mb: 2 }}>
            {t('dataAgents.form.clientName')}
            <Box component="span" sx={{ color: 'red', ml: 0.5 }}>
              *
            </Box>
          </Typography>
          {isClientIdDisabled ? (
            <>
              <TextField
                value={
                  userRole === 'clientadmin'
                    ? clientData?.name || ''
                    : entityType === 'edit' && values.client_id
                      ? sortedFilteredClientList.find((client) => client.client_id === values.client_id)?.name || ''
                      : ''
                }
                disabled
                fullWidth
                size="small"
                placeholder={clientLoading ? t('common.loading') : t('dataAgents.form.clientNamePlaceholder')}
                error={Boolean(touched.client_id && errors.client_id)}
                helperText={touched.client_id && errors.client_id ? getErrorMessage(errors.client_id) : ''}
              />
              <input type="hidden" name="client_id" value={values.client_id || ''} />
            </>
          ) : (
            <Select
              name="client_id"
              value={values.client_id}
              onChange={handleChange}
              onBlur={handleBlur}
              fullWidth
              size="small"
              displayEmpty
              error={Boolean(touched.client_id && errors.client_id)}
              disabled={dynamicClientLoading || !values.customer_id}
              MenuProps={{
                PaperProps: { style: { maxHeight: 400, zIndex: 1300 } },
                disablePortal: false,
              }}
            >
              <MenuItem value="">{t('dataAgents.form.selectClient')}</MenuItem>
              {sortedFilteredClientList.map((client) => (
                <MenuItem key={client.client_id} value={client.client_id}>
                  {client.name}
                </MenuItem>
              ))}
            </Select>
          )}
          {touched.client_id && errors.client_id && !isClientIdDisabled && (
            <Typography sx={{ color: 'error.main', fontSize: '0.75rem', mt: 1 }}>
              {getErrorMessage(errors.client_id)}
            </Typography>
          )}
        </Grid>
      </>
    );
  };

  return (
    <>
      {/* Main Form Container */}
      <Box sx={{ color: '#475569', width: '100%', p: 1, px: 2 }}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize={true}
          onSubmit={(values, formikHelpers) => {
            onSubmit(values, formikHelpers);
          }}
        >
          {({ values, handleChange, handleBlur, errors, touched, isSubmitting, setFieldValue }) => {
            return (
              <>
                <Form noValidate>
                  <Box
                    sx={{
                      py: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                    }}
                  >
                    <Typography variant="h5" component="div" sx={{ display: 'flex', alignItems: 'center', mb: 0 }}>
                      <IconButton disabled={isSubmitting} aria-label={t('common.back')} onClick={() => navigate(-1)}>
                        <ChevronLeft />
                      </IconButton>
                      {entityType === 'edit' ? t('dataAgents.form.editTitle') : t('dataAgents.form.addTitle')}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                      <Button
                        disabled={isSubmitting}
                        variant="contained"
                        color="primary"
                        type="submit"
                        sx={{ width: '180px', height: '40px', textTransform: 'none' }}
                      >
                        {entityType === 'edit' ? t('dataAgents.form.updateButton') : t('dataAgents.form.saveButton')}
                      </Button>
                    </Box>
                  </Box>

                  {/* Conditional Tabs - Only show tabs when in edit mode, otherwise show content directly */}
                  {isEditMode ? (
                    <>
                      {/* Master Tab */}
                      <TabPanel value={tabValue} index={0}>
                        <Grid container rowSpacing={4} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                          {/* Data Agent Name */}
                          <Grid size={6}>
                            <Typography component="p" sx={{ mb: 2 }}>
                              {t('dataAgents.form.name')}
                              <Box component="span" sx={{ color: 'red', ml: 0.5 }}>
                                *
                              </Box>
                            </Typography>
                            <TextField
                              name="name"
                              value={values.name}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              fullWidth
                              size="small"
                              placeholder={t('dataAgents.form.namePlaceholder')}
                              error={Boolean(touched.name && errors.name)}
                              helperText={touched.name && errors.name ? getErrorMessage(errors.name) : ''}
                            />
                          </Grid>

                          {/* Customer and Client Fields - NOW BELOW Icon and Name */}
                          {(userRole === 'superadmin' || userRole === 'customeradmin') &&
                            !shouldHideCustomerClientFields && (
                              <CustomerClientFields
                                values={values}
                                handleChange={handleChange}
                                handleBlur={handleBlur}
                                errors={errors}
                                touched={touched}
                                setFieldValue={setFieldValue}
                              />
                            )}

                          {/* Description */}
                          <Grid size={12}>
                            <Typography component="p" sx={{ mb: 2 }}>
                              {t('dataAgents.form.description')}
                            </Typography>
                            <TextField
                              name="description"
                              value={values.description}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              fullWidth
                              size="small"
                              placeholder={t('dataAgents.form.descriptionPlaceholder')}
                              multiline
                              rows={3}
                              error={Boolean(touched.description && errors.description)}
                              helperText={
                                touched.description && errors.description ? getErrorMessage(errors.description) : ''
                              }
                            />
                          </Grid>

                          {/* Instructions */}
                          <Grid size={12}>
                            <Typography
                              component="p"
                              sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                            >
                              <Box>
                                {t('dataAgents.form.instructions')}
                                <Box component="span" sx={{ color: 'red', ml: 0.5 }}>
                                  *
                                </Box>
                              </Box>
                              <Tooltip title={t('commonTooltips.enterFullscreen')}>
                                <IconButton size="small" onClick={() => setFullScreenField('instructions')}>
                                  <Maximize2 size={18} />
                                </IconButton>
                              </Tooltip>
                            </Typography>
                            <TextField
                              name="instructions"
                              value={values.instructions}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              fullWidth
                              size="small"
                              placeholder={t('dataAgents.form.instructionsPlaceholder')}
                              multiline
                              rows={4}
                              error={Boolean(touched.instructions && errors.instructions)}
                              helperText={
                                touched.instructions && errors.instructions ? getErrorMessage(errors.instructions) : ''
                              }
                            />
                          </Grid>

                          {/* System Prompt */}
                          <Grid size={12}>
                            <Typography
                              component="p"
                              sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                            >
                              {t('dataAgents.form.systemPrompt')}
                              <Tooltip title={t('commonTooltips.enterFullscreen')}>
                                <IconButton size="small" onClick={() => setFullScreenField('system_prompt')}>
                                  <Maximize2 size={18} />
                                </IconButton>
                              </Tooltip>
                            </Typography>
                            <TextField
                              name="system_prompt"
                              value={values.system_prompt}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              fullWidth
                              size="small"
                              placeholder={t('dataAgents.form.systemPromptPlaceholder')}
                              multiline
                              rows={4}
                            />
                          </Grid>

                          {/* Max Tokens */}
                          <Grid size={6}>
                            <Typography component="p" sx={{ mb: 2 }}>
                              {t('dataAgents.form.maxTokens')}
                              <Box component="span" sx={{ color: 'red', ml: 0.5 }}>
                                *
                              </Box>
                            </Typography>
                            <TextField
                              name="max_tokens"
                              type="number"
                              value={values.max_tokens}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              fullWidth
                              size="small"
                              placeholder={t('dataAgents.form.maxTokensPlaceholder')}
                              error={Boolean(touched.max_tokens && errors.max_tokens)}
                              helperText={
                                touched.max_tokens && errors.max_tokens ? getErrorMessage(errors.max_tokens) : ''
                              }
                              inputProps={{ min: 1, max: 128000 }}
                            />
                          </Grid>

                          {/* Temperature */}
                          <Grid size={6}>
                            <Typography component="p" sx={{ mb: 2 }}>
                              {t('dataAgents.form.temperature')}
                              <Box component="span" sx={{ color: 'red', ml: 0.5 }}>
                                *
                              </Box>
                            </Typography>
                            <TextField
                              name="temperature"
                              type="number"
                              value={values.temperature}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              fullWidth
                              size="small"
                              placeholder={t('dataAgents.form.temperaturePlaceholder')}
                              error={Boolean(touched.temperature && errors.temperature)}
                              helperText={
                                touched.temperature && errors.temperature ? getErrorMessage(errors.temperature) : ''
                              }
                              inputProps={{ min: 0, max: 2, step: 0.1 }}
                            />
                          </Grid>

                          {/* Is Active Toggle */}
                          <Grid size={6}>
                            <FormControlLabel
                              control={
                                <Switch
                                  name="is_active"
                                  checked={values.is_active}
                                  onChange={(e) => setFieldValue('is_active', e.target.checked)}
                                  disabled={isSubmitting}
                                />
                              }
                              label={t('dataAgents.form.isActive')}
                            />
                          </Grid>

                          {/* Is Published Toggle */}
                          <Grid size={6}>
                            <FormControlLabel
                              control={
                                <Switch
                                  name="is_published"
                                  checked={values.is_published}
                                  onChange={(e) => setFieldValue('is_published', e.target.checked)}
                                  disabled={isSubmitting}
                                />
                              }
                              label={t('dataAgents.form.isPublished')}
                            />
                          </Grid>
                        </Grid>
                      </TabPanel>
                    </>
                  ) : (
                    /* Add Mode - Show form directly without tabs */
                    <Grid container rowSpacing={4} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                      {/* Data Agent Name */}
                      <Grid size={6}>
                        <Typography component="p" sx={{ mb: 2 }}>
                          {t('dataAgents.form.name')}
                          <Box component="span" sx={{ color: 'red', ml: 0.5 }}>
                            *
                          </Box>
                        </Typography>
                        <TextField
                          name="name"
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          fullWidth
                          size="small"
                          placeholder={t('dataAgents.form.namePlaceholder')}
                          error={Boolean(touched.name && errors.name)}
                          helperText={touched.name && errors.name ? getErrorMessage(errors.name) : ''}
                        />
                      </Grid>

                      {/* Customer and Client Fields - NOW BELOW Icon and Name */}
                      {(userRole === 'superadmin' || userRole === 'customeradmin') &&
                        !shouldHideCustomerClientFields && (
                          <CustomerClientFields
                            values={values}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            errors={errors}
                            touched={touched}
                            setFieldValue={setFieldValue}
                          />
                        )}

                      {/* Description */}
                      <Grid size={12}>
                        <Typography component="p" sx={{ mb: 2 }}>
                          {t('dataAgents.form.description')}
                        </Typography>
                        <TextField
                          name="description"
                          value={values.description}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          fullWidth
                          size="small"
                          placeholder={t('dataAgents.form.descriptionPlaceholder')}
                          multiline
                          rows={3}
                          error={Boolean(touched.description && errors.description)}
                          helperText={
                            touched.description && errors.description ? getErrorMessage(errors.description) : ''
                          }
                        />
                      </Grid>

                      {/* Instructions */}
                      <Grid size={12}>
                        <Typography
                          component="p"
                          sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                        >
                          <Box>
                            {t('dataAgents.form.instructions')}
                            <Box component="span" sx={{ color: 'red', ml: 0.5 }}>
                              *
                            </Box>
                          </Box>
                          <Tooltip title={t('commonTooltips.enterFullscreen')}>
                            <IconButton size="small" onClick={() => setFullScreenField('instructions')}>
                              <Maximize2 size={18} />
                            </IconButton>
                          </Tooltip>
                        </Typography>
                        <TextField
                          name="instructions"
                          value={values.instructions}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          fullWidth
                          size="small"
                          placeholder={t('dataAgents.form.instructionsPlaceholder')}
                          multiline
                          rows={4}
                          error={Boolean(touched.instructions && errors.instructions)}
                          helperText={
                            touched.instructions && errors.instructions ? getErrorMessage(errors.instructions) : ''
                          }
                        />
                      </Grid>

                      {/* System Prompt */}
                      <Grid size={12}>
                        <Typography
                          component="p"
                          sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                        >
                          {t('dataAgents.form.systemPrompt')}
                          <Tooltip title={t('commonTooltips.enterFullscreen')}>
                            <IconButton size="small" onClick={() => setFullScreenField('system_prompt')}>
                              <Maximize2 size={18} />
                            </IconButton>
                          </Tooltip>
                        </Typography>
                        <TextField
                          name="system_prompt"
                          value={values.system_prompt}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          fullWidth
                          size="small"
                          placeholder={t('dataAgents.form.systemPromptPlaceholder')}
                          multiline
                          rows={4}
                        />
                      </Grid>

                      {/* Max Tokens */}
                      <Grid size={6}>
                        <Typography component="p" sx={{ mb: 2 }}>
                          {t('dataAgents.form.maxTokens')}
                          <Box component="span" sx={{ color: 'red', ml: 0.5 }}>
                            *
                          </Box>
                        </Typography>
                        <TextField
                          name="max_tokens"
                          type="number"
                          value={values.max_tokens}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          fullWidth
                          size="small"
                          placeholder={t('dataAgents.form.maxTokensPlaceholder')}
                          error={Boolean(touched.max_tokens && errors.max_tokens)}
                          helperText={touched.max_tokens && errors.max_tokens ? getErrorMessage(errors.max_tokens) : ''}
                          inputProps={{ min: 1, max: 128000 }}
                        />
                      </Grid>

                      {/* Temperature */}
                      <Grid size={6}>
                        <Typography component="p" sx={{ mb: 2 }}>
                          {t('dataAgents.form.temperature')}
                          <Box component="span" sx={{ color: 'red', ml: 0.5 }}>
                            *
                          </Box>
                        </Typography>
                        <TextField
                          name="temperature"
                          type="number"
                          value={values.temperature}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          fullWidth
                          size="small"
                          placeholder={t('dataAgents.form.temperaturePlaceholder')}
                          error={Boolean(touched.temperature && errors.temperature)}
                          helperText={
                            touched.temperature && errors.temperature ? getErrorMessage(errors.temperature) : ''
                          }
                          inputProps={{ min: 0, max: 2, step: 0.1 }}
                        />
                      </Grid>

                      {/* Is Active Toggle */}
                      <Grid size={6}>
                        <FormControlLabel
                          control={
                            <Switch
                              name="is_active"
                              checked={values.is_active}
                              onChange={(e) => setFieldValue('is_active', e.target.checked)}
                              disabled={isSubmitting}
                            />
                          }
                          label={t('dataAgents.form.isActive')}
                        />
                      </Grid>

                      {/* Is Published Toggle */}
                      <Grid size={6}>
                        <FormControlLabel
                          control={
                            <Switch
                              name="is_published"
                              checked={values.is_published}
                              onChange={(e) => setFieldValue('is_published', e.target.checked)}
                              disabled={isSubmitting}
                            />
                          }
                          label={t('dataAgents.form.isPublished')}
                        />
                      </Grid>
                    </Grid>
                  )}
                </Form>

                <FullScreenContentDialog
                  open={Boolean(fullScreenField)}
                  onClose={() => setFullScreenField(null)}
                  title={
                    fullScreenField === 'instructions'
                      ? t('dataAgents.form.instructions')
                      : t('dataAgents.form.systemPrompt')
                  }
                  exitTooltip={t('commonTooltips.exitFullscreen')}
                >
                  <TextField
                    name={fullScreenField || undefined}
                    value={fullScreenField ? values[fullScreenField] : ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    fullWidth
                    multiline
                    minRows={20}
                    placeholder={
                      fullScreenField === 'instructions'
                        ? t('dataAgents.form.instructionsPlaceholder')
                        : t('dataAgents.form.systemPromptPlaceholder')
                    }
                    sx={{
                      height: 'auto',
                      '& .MuiInputBase-root': {
                        height: 'auto',
                        alignItems: 'flex-start',
                      },
                    }}
                  />
                </FullScreenContentDialog>
              </>
            );
          }}
        </Formik>
      </Box>
    </>
  );
};

export default AddEditDataAgentUIForm;
